/* ═══════════════════════════════════════════════════════════════
   usePref — the ONE reusable write-through for per-user UI state
   (D1 cross-surface parity law, DESIGN.md §4 "Per-user persistence").

   localStorage is the fast first-paint seed (and keeps working before
   login / offline). The cloud `user_prefs` copy — via `Prefs`
   (prefsService.ts) — wins ONCE, the first render after `Prefs.isReady()`
   flips true, and is mirrored back to localStorage so the next launch (or
   a pre-login screen) still has it. Every write updates localStorage
   immediately and calls `Prefs.set` (which debounces 800ms and — since
   prefsService.ts centralizes it — silently skips the cloud write when
   the QA harness's `sf.dockNoCloudPersist` flag is set).

   Pattern copied from DataTable.tsx:880-996 and
   pages/people/DirectoryTab.tsx:232-249 (server-wins-once on mount, local
   seed first) — do not re-invent a second cache.

   `usePref` is for components. `readPref`/`writePref` are the same
   semantics for non-React modules (e.g. uiScale.ts) that can't call a hook.
   ═══════════════════════════════════════════════════════════════ */
import { useCallback, useEffect, useRef, useState } from 'react';
import type { Dispatch, SetStateAction } from 'react';
import { Prefs } from '../services/prefsService';

export interface UsePrefOpts<T> {
  /** Local storage is a raw string; default is `JSON.parse`. */
  parse?: (raw: string) => T;
  /** Default is `JSON.stringify`. */
  serialize?: (value: T) => string;
}

function readLocal<T>(localKey: string, fallback: T, parse?: (raw: string) => T): T {
  try {
    const raw = window.localStorage.getItem(localKey);
    if (raw == null) return fallback;
    return parse ? parse(raw) : (JSON.parse(raw) as T);
  } catch {
    return fallback;
  }
}

function writeLocal<T>(localKey: string, value: T, serialize?: (value: T) => string): void {
  try {
    window.localStorage.setItem(localKey, serialize ? serialize(value) : JSON.stringify(value));
  } catch {
    /* private mode / storage unavailable — the in-memory value still applies */
  }
}

/** Read a pref outside React: the cloud value once `Prefs` is ready, else
 *  the localStorage seed. Same server-wins-once intent as `usePref`, but
 *  evaluated fresh on every call (no subscription) — callers that need to
 *  react to `Prefs` becoming ready later should call this again from
 *  wherever they already hook into `Prefs.subscribe` / `Prefs.load`. */
export function readPref<T>(
  prefKey: string,
  localKey: string,
  fallback: T,
  opts?: UsePrefOpts<T>
): T {
  if (Prefs.isReady()) {
    const cloud = Prefs.get(prefKey);
    if (cloud !== undefined && cloud !== null) return cloud as T;
  }
  return readLocal(localKey, fallback, opts?.parse);
}

/** Write-through: localStorage immediately, `Prefs.set` debounced to the
 *  cloud (harness-guarded centrally in prefsService.ts). */
export function writePref<T>(
  prefKey: string,
  localKey: string,
  value: T,
  opts?: UsePrefOpts<T>
): void {
  writeLocal(localKey, value, opts?.serialize);
  Prefs.set(prefKey, value);
}

/** One per-user preference, write-through and cross-device. The setter
 *  mirrors `useState`'s signature (functional updates included) so it
 *  drops into existing `useState<T>` call sites with a one-line swap. */
export function usePref<T>(
  prefKey: string,
  localKey: string,
  fallback: T,
  opts?: UsePrefOpts<T>
): [T, Dispatch<SetStateAction<T>>] {
  // Latest opts/fallback in a ref so the memoized setter below never closes
  // over a stale parse/serialize function without forcing callers to memo
  // their `opts` object.
  const optsRef = useRef(opts);
  optsRef.current = opts;

  const [value, setValue] = useState<T>(() => readLocal(localKey, fallback, opts?.parse));
  const appliedRef = useRef(false);
  const [, forceTick] = useState(0);

  useEffect(() => Prefs.subscribe(() => forceTick((t) => t + 1)), []);

  // Guarded, re-checked every render (cheap) — the exact DirectoryTab.tsx
  // showArchived pattern: apply the cloud value exactly once, the first
  // render where `Prefs.isReady()` is true.
  useEffect(() => {
    if (appliedRef.current || !Prefs.isReady()) return;
    appliedRef.current = true;
    const cloud = Prefs.get(prefKey);
    if (cloud !== undefined && cloud !== null) {
      setValue(cloud as T);
      writeLocal(localKey, cloud as T, optsRef.current?.serialize);
    }
  });

  const set = useCallback<Dispatch<SetStateAction<T>>>(
    (next) => {
      setValue((prev) => {
        const resolved = typeof next === 'function' ? (next as (p: T) => T)(prev) : next;
        writeLocal(localKey, resolved, optsRef.current?.serialize);
        Prefs.set(prefKey, resolved);
        return resolved;
      });
    },
    [prefKey, localKey]
  );

  return [value, set];
}
